import { GUIDE_TIP_KEYS, type GuideTipKey } from "@ckgo2/shared";
import type { GuideInterviewItem, GuideTips } from "@ckgo2/shared/types";

/**
 * Operace editoru rozhovoru a tipů průvodce (`GuideInterviewEditor`, `GuideTipsFields`). Čisté
 * funkce bez Reactu (`guideProfileModel.test.ts`); vstup nikdy nemění, vždy vrací nový seznam
 * nebo objekt (precedent `toggleId` v `DestinationPicker.model.ts`).
 *
 * Texty jsou prostý text (`GuideInterviewItem`, `GuideTips`). Hodnota se při psaní neořezává
 * (ořez by snědl mezeru mezi slovy); `trim` dělá resolver `guideSchema`.
 */

/** Nová prázdná otázka na konci seznamu. */
export function addInterviewItem(items: readonly GuideInterviewItem[]): GuideInterviewItem[] {
  return [...items, { question: "", answer: "" }];
}

/** Seznam bez položky `index`; index mimo rozsah vrátí kopii beze změny. */
export function removeInterviewItem(
  items: readonly GuideInterviewItem[],
  index: number,
): GuideInterviewItem[] {
  return items.filter((_, i) => i !== index);
}

/** Prohodí položku `index` se sousedem ve směru `direction`; na okraji kopie beze změny. */
export function moveInterviewItem(
  items: readonly GuideInterviewItem[],
  index: number,
  direction: -1 | 1,
): GuideInterviewItem[] {
  const next = [...items];
  const target = index + direction;
  if (index < 0 || index >= next.length || target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Seznam se změněným textem `key` položky `index` (hodnota beze změny). */
export function updateInterviewItem(
  items: readonly GuideInterviewItem[],
  index: number,
  key: keyof GuideInterviewItem,
  value: string,
): GuideInterviewItem[] {
  return items.map((item, i) => (i === index ? { ...item, [key]: value } : item));
}

/** Pole tipů v pořadí editoru (vize F6.4): sdílený výčet `@ckgo2/shared` (čte ho i API a web). */
export { GUIDE_TIP_KEYS, type GuideTipKey };

/**
 * Tipy se změněným polem `key`. Když po změně žádné pole nemá text (po `trim`), vrátí `null`:
 * resolver by z prázdných polí udělal `{}` a sloupec by nesl prázdný objekt místo „bez tipů“;
 * `null` smazání posledního tipu opravdu uloží.
 */
export function updateTip(
  tips: GuideTips | null,
  key: GuideTipKey,
  value: string,
): GuideTips | null {
  const next: GuideTips = { ...(tips ?? {}), [key]: value };
  const hasText = GUIDE_TIP_KEYS.some((k) => (next[k] ?? "").trim() !== "");
  return hasText ? next : null;
}
